"use client";

import { METADATA_EXPAND_SCROLL_MARGIN_TOP } from "@/lib/constants/layout";
import { secureMarkdownSchema } from "@/lib/utils/sanitization";
import type { GameWithRelations } from "@/types/game";
import { forwardRef } from "react";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";

/** Prose styling for SEO / long-form copy inside {@link GameMetadataExpandShell}. */
export const GAME_METADATA_MARKDOWN_PROSE_CLASS =
  "prose prose-base sm:prose-lg prose-invert max-w-none break-words prose-headings:text-white prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-h4:text-base prose-ul:list-disc prose-ol:list-decimal prose-li:list-item prose-li:marker:text-white prose-hr:border-white/30 prose-p:text-white/90 prose-a:text-white/80 prose-a:underline prose-strong:text-white prose-code:text-white/90 prose-pre:max-w-full prose-pre:overflow-x-auto prose-pre:bg-white/10 prose-pre:text-white/90 prose-blockquote:text-white/80 prose-blockquote:border-white/30 prose-img:max-w-full prose-img:h-auto";

/** Same expand treatment as game pages: motion + tinted card wrapper. */
export const GameMetadataExpandShell = forwardRef<HTMLDivElement, { children: React.ReactNode }>(
  function GameMetadataExpandShell({ children }, ref) {
    return (
      <div
        ref={ref}
        className="mt-4 animate-in slide-in-from-top-2 duration-200"
        style={{ scrollMarginTop: METADATA_EXPAND_SCROLL_MARGIN_TOP }}
      >
        <div
          className="mt-6 bg-ink-light/90 rounded-2xl p-6 space-y-6"
          data-testid="metadata-expand-card"
        >
          {children}
        </div>
      </div>
    );
  }
);

interface GameMetadataSectionProps {
  game: GameWithRelations;
}

export function GameMetadataSection({ game }: GameMetadataSectionProps) {
  const hasSeoContent = !!game.seo_content;
  const hasPublisher = !!game.publisher;
  // game.tags exist on the model but are not shown in this expandable panel for now.

  // If no metadata to show, return null
  if (!hasSeoContent && !hasPublisher) {
    return null;
  }

  return (
    <>
      {/* SEO Markdown Content */}
      {hasSeoContent && (
        <div className={GAME_METADATA_MARKDOWN_PROSE_CLASS} data-testid="game-seo-content">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[[rehypeSanitize, secureMarkdownSchema]]}
          >
            {game.seo_content ?? ""}
          </ReactMarkdown>
        </div>
      )}

      {/* Publisher Section */}
      {hasPublisher && (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-white">Publisher</h3>
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            {game.publisher?.logo_url && (
              <img
                src={game.publisher.logo_url}
                alt={`${game.publisher?.name ?? "Publisher"} logo`}
                className="h-8 w-auto max-w-[7rem] shrink-0 object-contain sm:max-w-[9rem]"
              />
            )}
            <div className="min-w-0 break-words">
              <p className="text-white font-medium">{game.publisher?.name ?? ""}</p>
              {game.publisher?.website_url && (
                <a
                  href={game.publisher.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-white text-sm underline"
                >
                  Visit Website
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
